/**
 * Hook over the Playground **app index** (`playground.apps`): the derived,
 * incrementally-maintained view of every App (directory with a
 * `playground.json`) in the workspace. Built-in (mounted) Apps and workspace
 * Apps surface identically, and each entry already carries the display name,
 * icon, and parsed manifest, so consumers never read a manifest file themselves.
 *
 * Entries are keyed by **path** (the launcher identity): two installed copies
 * of the same App are two launchable entries. The `@publisher/name` registry
 * identity is only for agent tool routing.
 */
import type { AppIndexEntry } from "playground/apps/index/app-index";
import { appPageEntrypoint, resolveAppPage } from "playground/apps/manifest";
import type { Playground } from "playground/hal/index";
import { useEffect, useState } from "react";

export interface AppIndexState {
  /** Every indexed App, sorted by path (stable across replicas). */
  entries: AppIndexEntry[];
  /**
   * `false` until the index's initial catch-up (`ready()`) resolves.
   * Distinguishes "still catching up" (show a loading state) from "caught up,
   * genuinely empty/missing". Callers must not treat a not-yet-found path as
   * "missing" while this is false.
   */
  loaded: boolean;
}

/**
 * `playground` is nullable because a host may call this while *building* the
 * context its Playground lives in, before that Playground exists.
 */
export function useAppIndex(playground: Playground | null): AppIndexState {
  const [state, setState] = useState<AppIndexState>({
    entries: [],
    loaded: false,
  });

  useEffect(() => {
    if (!playground) {
      setState({ entries: [], loaded: false });
      return;
    }
    const index = playground.apps;
    let cancelled = false;
    // `loaded` flips true only once `ready()` resolves; mutations that land
    // before then still refresh `entries` (a partially caught-up list is
    // better than an empty one) without prematurely claiming completeness.
    let ready = false;
    const sync = () => {
      if (!cancelled) setState({ entries: index.entries(), loaded: ready });
    };
    const unsubscribe = index.subscribe(sync);
    sync();
    void index.ready().then(() => {
      ready = true;
      sync();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [playground]);

  return state;
}

/** True when the App ships a full-page `page` entrypoint (launchable). */
export function hasPage(entry: AppIndexEntry): boolean {
  return (
    entry.manifest !== undefined &&
    resolveAppPage(appPageEntrypoint(entry.manifest)?.entry.module).kind !==
      "none"
  );
}

/**
 * The Apps to surface in a launcher: only page-bearing (launchable) Apps,
 * ordered alphabetically by display name so the list is stable between visits
 * (no recency-driven reshuffling).
 */
export function launchableApps(entries: AppIndexEntry[]): AppIndexEntry[] {
  return entries
    .filter(hasPage)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * The App whose directory contains `path` (or is `path`), if any: the
 * opacity question FS-aware surfaces ask ("am I inside an App?"). Pure over
 * the reactive `entries` so callers re-derive when the index changes. Nested
 * Apps never index (macOS bundle model), so at most one entry can match.
 */
export function appAt(
  entries: AppIndexEntry[],
  path: string,
): AppIndexEntry | undefined {
  return entries.find((e) => path === e.path || path.startsWith(`${e.path}/`));
}
